import { doctor, areasServed, faqs, contact, treatments, oriental } from "../data/content";

/** Strips empty strings/arrays/objects so nothing is published as a guess. */
function prune(value) {
  if (Array.isArray(value)) {
    const arr = value.map(prune).filter((v) => v !== undefined);
    return arr.length ? arr : undefined;
  }
  if (value && typeof value === "object") {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      const cleaned = prune(v);
      if (cleaned !== undefined) out[k] = cleaned;
    });
    // an object holding only its @type carries no information
    return Object.keys(out).filter((k) => k !== "@type").length ? out : undefined;
  }
  if (typeof value === "string") return value.trim() ? value.trim() : undefined;
  return value ?? undefined;
}

/** "Monday – Friday" / "9:00 — 19:00" → schema.org opening hours. */
function openingHours() {
  const dayMap = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };
  const order = Object.values(dayMap);

  return contact.hours
    .filter((h) => !/closed/i.test(h.time))
    .map((h) => {
      const found = Object.keys(dayMap).filter((d) =>
        h.day.toLowerCase().includes(d)
      );
      let days = found.map((d) => dayMap[d]);

      // "Monday – Friday" means the inclusive range, not just the ends
      if (/[–—-]/.test(h.day) && days.length === 2) {
        const [a, b] = [order.indexOf(days[0]), order.indexOf(days[1])];
        if (a > -1 && b > a) days = order.slice(a, b + 1);
      }
      if (!days.length) return undefined;

      const times = h.time.match(/(\d{1,2}):(\d{2})/g);
      if (!times || times.length < 2) return undefined;

      return {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: days,
        opens: times[0].padStart(5, "0"),
        closes: times[1].padStart(5, "0"),
      };
    })
    .filter(Boolean);
}

/**
 * Physiotherapy practice as a LocalBusiness.
 * `Physiotherapy` is a recognised schema.org MedicalBusiness subtype,
 * which is more specific — and better for local packs — than a bare
 * LocalBusiness.
 */
export function businessSchema(siteUrl) {
  const services = [
    ...treatments.items.map((t) => t.title),
    ...oriental.items.map((o) => o.title),
  ];

  return prune({
    "@context": "https://schema.org",
    "@type": ["Physiotherapy", "MedicalBusiness", "LocalBusiness"],
    "@id": `${siteUrl}/#practice`,
    name: `${doctor.name} — Physiotherapist`,
    description:
      "Physiotherapy clinic in Gurugram offering orthopedic, sports and neurological rehabilitation, pre and post surgical rehab, osteopathy, dry needling and manual therapy. Clinic and home visits.",
    url: siteUrl,
    telephone: doctor.phoneHref,
    email: doctor.email,
    image: `${siteUrl}/og-image.jpg`,
    logo: `${siteUrl}/logo512.png`,
    priceRange: "₹₹",
    currenciesAccepted: "INR",
    address: {
      "@type": "PostalAddress",
      streetAddress: doctor.address.streetAddress,
      addressLocality: doctor.address.locality,
      addressRegion: doctor.address.region,
      postalCode: doctor.address.postalCode,
      addressCountry: doctor.address.country,
    },
    geo:
      doctor.geo.lat && doctor.geo.lng
        ? {
            "@type": "GeoCoordinates",
            latitude: doctor.geo.lat,
            longitude: doctor.geo.lng,
          }
        : undefined,
    areaServed: areasServed.map((a) => ({
      "@type": "Place",
      name: `${a}, Gurugram`,
    })),
    openingHoursSpecification: openingHours(),
    sameAs: Object.values(doctor.profiles).filter(Boolean),
    medicalSpecialty: "Physiotherapy",
    availableService: services.map((s) => ({
      "@type": "MedicalTherapy",
      name: s,
    })),
    founder: {
      "@type": "Person",
      name: doctor.name,
      jobTitle: doctor.role,
      honorificSuffix: doctor.credential,
    },
  });
}

export function faqSchema() {
  return prune({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });
}

export function breadcrumbSchema(siteUrl, pathname, label) {
  if (pathname === "/") return undefined;
  return prune({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: label,
        item: `${siteUrl}${pathname}`,
      },
    ],
  });
}
